import React, { SetStateAction, useEffect, useState } from 'react'
import { IconS24LocationPin124 } from '@is24/cosma-ui-icons'
import { AddressScreenProps } from '../../../../../@types/props-types'
import { SCREEN_ADDRESS, SCREEN_CONSTRUCTION, SCREENS } from '../../../../../services/domain/wizardScreens'
import { AddressScreenValidationResult } from '../../../../../@types/validation-types'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { defaultPageIntersectionOptions } from '../withNavigateOnInView'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import NationalAddress from './nationalAddress/NationalAddress'
import AddressSwitcher from './addressSwitcher/AddressSwitcher'
import InternationalAddress from './internationalAddress/InternationalAddress'
import { doResetAddressFeature } from '../../../../../redux/thunks/internationalAddress/internationalAddress'
import { InitState } from '../../../../../redux/reducers/root/initialState'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import ReadOnlyAddress from './readOnlyAddress/ReadOnlyAddress'
import { isGermanCountryCode } from '../../../../../utils/geo-utils'
import styles from '../../../../common/containers/screenLayout/ScreenLayout.module.css'

const screen = SCREENS[SCREEN_ADDRESS]

const Address: React.FC<AddressScreenProps<AddressScreenValidationResult>> = ({
  insertionData,
  validationMessages,
  isInternationalAddressOption,
  onChange,
  onNavigate,
  doReset,
  flowInformation: _flowInformation,
}) => {
  const dispatch = useAppDispatch()
  const [isNational, setIsNational] = useState(true)
  const isContingentCustomer = useAppSelector((state: InitState) => state.userFeature.contingentCustomer)
  const [showReadOnlyAddress, setShowReadOnlyAddress] = useState(false)

  useEffect(() => {
    const isValid =
      validationMessages &&
      Object.values(validationMessages).every(
        (value) => (Array.isArray(value) && value.length === 0) || value === undefined
      )

    if (
      isValid &&
      insertionData.addressEditable != undefined &&
      !insertionData.addressEditable &&
      !isContingentCustomer
    ) {
      setShowReadOnlyAddress(true)
    } else {
      setShowReadOnlyAddress(false)
    }
  }, [validationMessages, insertionData.addressEditable, isContingentCustomer])

  useEffect(() => {
    if (insertionData.manualCountry?.value && !isGermanCountryCode(insertionData.manualCountry.value)) {
      setIsNational(false)
    }
  }, [insertionData.manualCountry?.value])

  const changeAddressSwitcher = async (value: SetStateAction<boolean>) => {
    doReset &&
      doReset([
        {
          screen: screen.name,
          property: 'addressId',
        },
        {
          screen: screen.name,
          property: 'street',
        },
        {
          screen: screen.name,
          property: 'houseNumber',
        },
        {
          screen: screen.name,
          property: 'hideStreetAndStreetNumber',
        },
        {
          screen: screen.name,
          property: 'postcode',
        },
        {
          screen: screen.name,
          property: 'city',
        },
        {
          screen: screen.name,
          property: 'manualCountry',
        },
        {
          screen: screen.name,
          property: 'manualRegion',
        },
        {
          screen: screen.name,
          property: 'manualQuarter',
        },
        {
          screen: SCREENS[SCREEN_CONSTRUCTION].name,
          property: 'energyCertificateAvailability',
        },
        {
          screen: SCREENS[SCREEN_CONSTRUCTION].name,
          property: 'buildingEnergyRatingType',
        },
        {
          screen: SCREENS[SCREEN_CONSTRUCTION].name,
          property: 'thermalCharacteristic',
        },
        {
          screen: SCREENS[SCREEN_CONSTRUCTION].name,
          property: 'efficiencyClass',
        },
        {
          screen: SCREENS[SCREEN_CONSTRUCTION].name,
          property: 'legalConstructionYear',
        },
      ])
    setIsNational(value)
    await dispatch(doResetAddressFeature(value as boolean))
  }

  function editableAddress() {
    return (
      <>
        {isInternationalAddressOption && (
          <AddressSwitcher isNational={isNational} setIsNational={changeAddressSwitcher} />
        )}
        {isNational ? (
          <NationalAddress
            insertionData={insertionData}
            validationMessages={validationMessages}
            onChange={onChange}
            doReset={doReset}
          />
        ) : (
          <InternationalAddress
            insertionData={insertionData}
            validationMessages={validationMessages}
            onChange={onChange}
          />
        )}
      </>
    )
  }

  return (
    <ScreenContainer
      containerId={screen.id}
      className='grid margin-bottom-l'
      onNavigate={onNavigate}
      screen={screen}
      intersectionOptions={{ ...defaultPageIntersectionOptions }}
    >
      <ScreenLayout
        id={`${screen.id}-desk-tooltip`}
        headline={'wizard.screen.address.header'}
        subLine={undefined}
        headerIcon={<IconS24LocationPin124 />}
        screenName={screen.name}
        sectionClassName={styles.allowOverflow}
      >
        {showReadOnlyAddress && <ReadOnlyAddress insertionData={insertionData} onChange={onChange} />}
        {!showReadOnlyAddress && editableAddress()}
      </ScreenLayout>
    </ScreenContainer>
  )
}

export default Address
